"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { SmartDateInput } from "@/components/ui/smart-date-input";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { useFollowingDefault } from "@/hooks/use-following-default";
import { type CalendarDay, type ClinicClock, dayMarker, pickedDay } from "@/lib/clinic-clock";
import { type ResolvedItem, findLatestPriorValue, resolveGroupMemberships } from "@/lib/measurement-utils";
import type { MeasurementGroupWithFields, MeasurementValueSummary, PrefillValue } from "@/types/measurement";
import RecordForm from "./record-form";
import { measurementQueryOptions, usePatientMeasurementsQuery } from "@/query/patient/measurements";
interface MeasurementRecordDialogProps {
  isOpen: boolean;
  onClose: () => void;
  group: MeasurementGroupWithFields;
  patientId: string;
  noteId?: string;
  eventId?: string;
  /** The clock the day is read on; the clinic's by default. */
  clock?: ClinicClock;
  /** The day the dialog opens on; today by default. */
  defaultDay?: CalendarDay;
}
function RecordFormSkeleton(t0) {
  const $ = _c(2);
  const {
    fieldCount
  } = t0;
  let t1;
  if ($[0] !== fieldCount) {
    t1 = <div className="space-y-4">{Array.from({
        length: fieldCount
      }).map(_temp)}</div>;
    $[0] = fieldCount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp(_, i) {
  return <div key={i} className="space-y-1"><Skeleton className="h-4 w-24" /><Skeleton className="h-9 w-full" /></div>;
}
export default function MeasurementRecordDialog({
  isOpen,
  onClose,
  group,
  patientId,
  noteId,
  eventId,
  clock: clockProp,
  defaultDay
}: MeasurementRecordDialogProps) {
  // The day being recorded, as the picker's marker. The moment saved is
  // worked out from it at save time (see computeSaveTimestamp). It starts on
  // today each time the dialog opens: callers keep it mounted while closed,
  // so a day set at mount would be stale by the next open.
  const clinicClock = useClinicClock();
  const clock = clockProp ?? clinicClock;
  const openingDay = useMemo(() => isOpen ? dayMarker(defaultDay ?? clock.today()) : undefined, [isOpen, defaultDay, clock]);
  const [measurementDate, setMeasurementDate] = useFollowingDefault(openingDay, isOpen);
  const resolvedItems = useMemo(() => resolveGroupMemberships(group, {
    excludeDerived: true
  }), [group]);
  const fieldItems = useMemo(() => resolvedItems.filter((item): item is Extract<ResolvedItem, {
    type: "field";
  }> => item.type === "field"), [resolvedItems]);
  const sections = useMemo(() => {
    const result: {
      header: Extract<ResolvedItem, {
        type: "section-header";
      }> | null;
      items: Extract<ResolvedItem, {
        type: "field";
      }>[];
    }[] = [];
    let current: (typeof result)[number] | null = null;
    for (const item_0 of resolvedItems) {
      if (item_0.type === "section-header") {
        current = {
          header: item_0,
          items: []
        };
        result.push(current);
      } else {
        if (!current) {
          current = {
            header: null,
            items: []
          };
          result.push(current);
        }
        current.items.push(item_0);
      }
    }
    return result;
  }, [resolvedItems]);
  const {
    startUtc,
    endUtc
  } = useMemo(() => {
    if (!measurementDate) return {
      startUtc: "",
      endUtc: ""
    };
    const {
      start,
      end
    } = clock.dayRange(pickedDay(measurementDate));
    return {
      startUtc: start.toISOString(),
      endUtc: new Date(end.getTime() - 1).toISOString()
    };
  }, [measurementDate, clock]);
  const fieldIds = useMemo(() => fieldItems.map(item_1 => item_1.field.id), [fieldItems]);
  const groupId = group.id || null;
  const prefillQuery = useQuery({
    ...measurementQueryOptions.prefill(patientId, groupId, fieldIds, startUtc, endUtc),
    enabled: isOpen && !!startUtc && !!patientId && (!!groupId || fieldIds.length > 0)
  });
  const prefillData = useMemo(() => {
    const map = new Map<string, PrefillValue>();
    if (prefillQuery.data?.values) {
      for (const pv of prefillQuery.data.values) {
        map.set(pv.fieldId, pv);
      }
    }
    return map;
  }, [prefillQuery.data]);
  const {
    data: patientData
  } = usePatientMeasurementsQuery(patientId);
  const lastPriorByField = useMemo(() => {
    const map_0 = new Map<string, MeasurementValueSummary>();
    if (!patientData?.values || !measurementDate) return map_0;
    for (const id of fieldIds) {
      const prior = findLatestPriorValue(patientData.values, id, clock.dayRange(pickedDay(measurementDate)).start, clock);
      if (prior) map_0.set(id, prior);
    }
    return map_0;
  }, [patientData?.values, measurementDate, fieldIds, clock]);
  return <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="record-dialog" onOpenAutoFocus={e => {
      e.preventDefault();
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.focus();
      }
    }}>
        <DialogHeader>
          <DialogTitle>Record {group.name}</DialogTitle>
          <DialogDescription>
            Enter measurement values for this group.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-4 pb-2 md:px-6">
          <div className="space-y-1">
            <Label>Measurement Date</Label>
            <SmartDateInput value={measurementDate} onChange={setMeasurementDate} direction="past" />
          </div>
        </div>

        {prefillQuery.isLoading ? <div className="px-4 pb-4">
            <RecordFormSkeleton fieldCount={fieldItems.length} />
          </div> : <RecordForm
      // A new day starts a new form; the clock moving keeps the draft.
      key={measurementDate ? pickedDay(measurementDate) : ""} group={group} patientId={patientId} noteId={noteId} eventId={eventId} measurementDate={measurementDate} clock={clock} sections={sections} fieldItems={fieldItems} prefillData={prefillData} lastPriorByField={lastPriorByField} onClose={onClose} />}
      </DialogContent>
    </Dialog>;
}